import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import TagManager from "react-gtm-module";
import { useAuthNextStep } from "../../../hooks/api/v2/auth/useAuthNextStep";
import { useAuthCreateSignupCode } from "../../../hooks/api/v2/auth/useAuthCreateSignupCode";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import {
  isSignInConnectionError,
  signInConnectionMessage,
} from "../../../hooks/signInConnection";
import { useFirebaseUser } from "../../../hooks/useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import {
  SignupFlowStep,
  useSignupContext,
} from "../../../hooks/useSignupContext.shared";
import { Mixpanel } from "../../../utils/mixpanel";

export const useSignupActions = ({
  setEmail,
  previousEmail,
  businessUserInviteJwt,
}: {
  setEmail: (email: string) => void;
  previousEmail?: string;
  businessUserInviteJwt: string | null;
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ title?: string; description: string }>();
  const submission = useRef(0);
  const busy = useRef(false);
  useEffect(
    () => () => {
      submission.current++;
      busy.current = false;
    },
    [],
  );
  const [searchParams] = useSearchParams();
  const { user } = useFirebaseUser();
  const { business } = useSellarCurrentBusiness();
  const { setStep } = useSignupContext();
  const navigate = useNavigate();
  const nextStepMutation = useAuthNextStep();
  const codeMutation = useAuthCreateSignupCode();
  const utmMedium = searchParams.get("utm_medium");

  const runSubmission = async (
    action: (isCurrent: () => boolean) => Promise<void>,
  ) => {
    if (busy.current) return;
    busy.current = true;
    const attempt = ++submission.current;
    const isCurrent = () => attempt === submission.current;
    setLoading(true);
    setError(undefined);
    try {
      await action(isCurrent);
    } catch (error) {
      if (isCurrent())
        setError({
          description: isSignInConnectionError(error)
            ? signInConnectionMessage
            : (apiV2AssertError(error)?.response?.data.message ??
              "Unable to continue. Please try again."),
        });
    } finally {
      if (isCurrent()) {
        busy.current = false;
        setLoading(false);
      }
    }
  };

  const continueSignIn = (email: string) => {
    setEmail(email);
    if (user && !user.hasPasswordAuth)
      return setStep(SignupFlowStep.SignupDetails);
    if (user && user.hasPasswordAuth && businessUserInviteJwt) {
      navigate(`/jwt?jwt=${businessUserInviteJwt}`);
      return;
    }
    if (user && !business) return setStep(SignupFlowStep.BuyerCreationStep);
    setStep(SignupFlowStep.SignInStep);
  };

  const createCode = (email: string) =>
    codeMutation.mutateAsync({
      data: { email, previousEmail },
      sellarMetadata: {
        storefrontLinkPassword:
          searchParams.get("storefrontLink") ??
          searchParams.get("priceTierLink"),
        businessUserInviteJwt,
        utmMedium,
        mixpanelDistinctId: Mixpanel.getDistinctId(),
      },
    });

  const trackSignup = (email: string) => {
    Mixpanel.alias(email);
    TagManager.dataLayer({
      dataLayer: { event: "email_address_entered", email },
    });
  };

  return {
    loading,
    error,
    setError,
    runSubmission,
    continueSignIn,
    createCode,
    trackSignup,
    getNextStep: nextStepMutation.mutateAsync,
    isInvite: Boolean(businessUserInviteJwt) && utmMedium !== "email",
  };
};
